import type { HTMLAttributes } from 'react';
import { cn } from '../lib/utils.js';

export function Skeleton({ className, ...props }: Readonly<HTMLAttributes<HTMLDivElement>>) {
  return (
    <div
      data-slot="skeleton"
      className={cn('bg-muted animate-pulse rounded-md', className)}
      {...props}
    />
  );
}

/**
 * SkeletonCard — placeholder composite mimicking um Card de conteúdo
 * (header + 3 linhas). Útil para listas de itens uniformes em loading.
 */
export function SkeletonCard({ className, ...props }: Readonly<HTMLAttributes<HTMLDivElement>>) {
  return (
    <div
      data-slot="skeleton-card"
      aria-busy="true"
      className={cn('border-border bg-card flex flex-col gap-3 rounded-lg border p-4', className)}
      {...props}
    >
      <Skeleton className="h-4 w-2/5" />
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-4/5" />
      <Skeleton className="h-3 w-3/5" />
    </div>
  );
}

/**
 * SkeletonRow — placeholder de linha de lista (avatar/ícone + 2 linhas
 * de texto à direita). Reduz CLS em listas verticais.
 */
export function SkeletonRow({ className, ...props }: Readonly<HTMLAttributes<HTMLDivElement>>) {
  return (
    <div
      data-slot="skeleton-row"
      aria-busy="true"
      className={cn('flex items-center gap-3 p-2', className)}
      {...props}
    >
      <Skeleton className="size-9 shrink-0 rounded-md" />
      <div className="flex flex-1 flex-col gap-1.5">
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </div>
  );
}

/**
 * SkeletonList — N `SkeletonRow` empilhados, com `aria-busy` no container.
 * Default 4 linhas.
 */
export function SkeletonList({
  count = 4,
  className,
  ...props
}: Readonly<HTMLAttributes<HTMLDivElement> & { count?: number }>) {
  return (
    <div
      data-slot="skeleton-list"
      aria-busy="true"
      className={cn('flex flex-col gap-1', className)}
      {...props}
    >
      {Array.from({ length: Math.max(1, count) }, (_, i) => (
        <SkeletonRow key={`skeleton-row-${String(i)}`} />
      ))}
    </div>
  );
}
